// Racine de l'application : session, puis routage.
//
// La SOURCE DE VÉRITÉ de la session est le serveur (`GET /api/me`), jamais un
// drapeau local. Le cookie étant httpOnly, le JavaScript ne peut pas le lire —
// c'est voulu : une XSS ne peut pas exfiltrer la session. Le corollaire est
// qu'il faut demander au serveur qui l'on est, et l'accepter comme réponse.
//
// Aucun écran n'a donc à prévenir la racine qu'il s'est connecté ou déconnecté :
// les mutations mettent le cache d'identité à jour, et le rendu suit.
import { AuthPage } from './auth/AuthPage';
import { PortfolioPage } from './portfolio/PortfolioPage';
import { ProjectDashboard } from './dashboard/ProjectDashboard';
import { ProjectLayout } from './project/ProjectLayout';
import {
  AuditsScreen,
  StudiesScreen,
  TestsScreen,
  TriangulationScreen,
} from './project/ListScreens';
import { InsightsScreen } from './project/InsightsScreen';
import { UsersAndPersonasScreen } from './people/PeopleScreen';
import { TestDetailScreen } from './project/TestDetailScreen';
import { StudyDetailScreen } from './project/StudyDetailScreen';
import { InterviewDetailScreen, InterviewsScreen } from './project/InterviewsScreen';
import { CopilotScreen } from './project/CopilotScreen';
import { StudioScreen } from './studio/StudioScreen';
import { AppShell } from './components/AppShell';
import { PublicQuestionnaire } from './mesure0/PublicQuestionnaire';
import { AcceptInvitationPage } from './options/AcceptInvitationPage';
import { OptionsScreen } from './options/OptionsScreen';
import { TaxonomyScreen } from './options/TaxonomySection';
import { PublicInvitationPage } from './invitations/PublicInvitationPage';
import { PublicStudyQuestionnaire } from './studies/PublicStudyQuestionnaire';
import { legalPageFor } from './legal/LegalPages';
import { CookieBanner } from './legal/CookieBanner';
import { BackofficePage } from './backoffice/BackofficePage';
import { AccountPage } from './account/AccountPage';
import { RoadmapPage, VersionsPage } from './updates/UpdatesPages';
import { ProjectGate } from './kickoff/KickoffPage';
import { BoardScreen } from './board/BoardScreen';
import { SourceCutScreen } from './sources/SourceCutScreen';
import { InviteToStudyPage, NewInterviewPage, NewStudyPage } from './studies/StudyBuilderPage';
import { PersonPage } from './people/PersonPage';
import { KIND_BY_KEY } from './studies/kinds';
import type { StudyKind } from './api/types';
import { BackofficeLogin } from './backoffice/BackofficeLogin';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { keys, useBootstrap, useMaintenance, useMe } from './api/queries';
import { MaintenancePage } from './maintenance/MaintenancePage';
import { Button, Spinner } from './components/ui';
import { matchRoute, useRouter } from './router';

/** Écran d'une section de projet. Une seule table, pour ne pas oublier de
 *  brancher un écran ajouté à la navigation. */
function sectionScreen(section: string, projectId: string, projectName: string) {
  switch (section) {
    case 'etudes':
      return <StudiesScreen projectId={projectId} />;
    case 'entretiens':
      return <InterviewsScreen projectId={projectId} />;
    case 'tests':
      return <TestsScreen projectId={projectId} />;
    case 'personas':
      return <UsersAndPersonasScreen projectId={projectId} />;
    case 'insights':
      return <InsightsScreen projectId={projectId} />;
    case 'atelier':
      return <BoardScreen projectId={projectId} />;
    case 'triangulation':
      return <TriangulationScreen projectId={projectId} />;
    case 'taxonomie':
      return <TaxonomyScreen projectId={projectId} />;
    case 'audits':
      return <AuditsScreen projectId={projectId} />;
    case 'studio':
      return <StudioScreen projectId={projectId} projectName={projectName} />;
    case 'copilote':
      return <CopilotScreen projectId={projectId} />;
    case 'options':
      return <OptionsScreen projectId={projectId} projectName={projectName} />;
    default:
      return <ProjectDashboard projectId={projectId} projectName={projectName} />;
  }
}

function Routes() {
  const { path } = useRouter();
  // Le portefeuille sert aussi de résolveur de nom : il est déjà en cache, et
  // c'est la seule source qui dise quels projets sont accessibles.
  const bootstrap = useBootstrap(true);

  const root = matchRoute('/projet/:id', path);
  const nested = matchRoute('/projet/:id/:section', path);
  // La fiche d'un test, et celle d'une étude, ont chacune leur propre chemin :
  // on peut les envoyer directement (validation d'un guide, relecture d'un
  // terrain) sans passer par la liste.
  const testDetail = matchRoute('/projet/:id/tests/:testId', path);
  const studyDetail = matchRoute('/projet/:id/etudes/:studyId', path);
  const interviewDetail = matchRoute('/projet/:id/entretiens/:interviewId', path);
  // L'atelier d'UNE étude : le même plan, filtré sur son terrain.
  const studyBoard = matchRoute('/projet/:id/atelier/:studyId', path);
  // Découper une transcription d'une étude (migration 047).
  const sourceCut = matchRoute('/projet/:id/etudes/:studyId/transcriptions/:sourceId', path);
  // Construire une étude en pleine page, inviter, organiser un entretien (055).
  // Avant la fiche d'étude : « nouvelle » n'est pas un identifiant d'étude.
  const studyNew = matchRoute('/projet/:id/etudes/nouvelle', path) ?? matchRoute('/projet/:id/etudes/nouvelle/:kind', path);
  const studyInvite = matchRoute('/projet/:id/etudes/:studyId/inviter', path);
  const interviewNew = matchRoute('/projet/:id/entretiens/nouveau', path);
  // La fiche d'une personne de l'annuaire.
  const personPage = matchRoute('/projet/:id/personnes/:personId', path);
  const match = root ?? nested ?? testDetail ?? studyDetail ?? interviewDetail ?? studyBoard ?? sourceCut
    ?? studyNew ?? studyInvite ?? personPage;

  if (match?.['id']) {
    const id = match['id'];
    const section = testDetail ? 'tests'
      : studyDetail || sourceCut || studyNew || studyInvite ? 'etudes'
        : interviewDetail || interviewNew ? 'entretiens'
          : personPage ? 'personas'
          : studyBoard ? 'atelier' : (nested?.['section'] ?? '');
    const project = bootstrap.data?.clients.flatMap((c) => c.projets).find((p) => p.id === id);

    // Tant que le portefeuille charge, on ne conclut pas à l'absence du projet.
    if (bootstrap.isPending) {
      return (
        <div className="app-loading">
          <Spinner label="Chargement…" />
        </div>
      );
    }
    // Projet hors périmètre : le serveur répondrait 404 de toute façon. On le
    // dit sans révéler s'il existe ailleurs.
    if (!project) {
      return (
        <div className="app-main">
          <h1 className="t-h2">Projet introuvable dans votre périmètre.</h1>
        </div>
      );
    }

    const openedTest = testDetail?.['testId'];
    const builderKind = studyNew?.['kind'];
    const openedStudy = studyNew ? undefined : studyDetail?.['studyId'];
    const openedInterview = interviewNew ? undefined : interviewDetail?.['interviewId'];
    const boardStudy = studyBoard?.['studyId'];
    const cutSource = sourceCut?.['sourceId'];
    return (
      // Un projet vide s'ouvre sur son parcours de démarrage, pleine page.
      <ProjectGate projectId={id} projectName={project.nom}>
        <ProjectLayout projectId={id} projectName={project.nom} section={section}>
          {studyNew ? (
            <NewStudyPage
              key={builderKind ?? 'libre'}
              projectId={id}
              presetKind={builderKind && builderKind in KIND_BY_KEY ? (builderKind as StudyKind) : null}
            />
          ) : studyInvite ? (
            <InviteToStudyPage projectId={id} studyId={studyInvite['studyId']!} />
          ) : interviewNew ? (
            <NewInterviewPage projectId={id} />
          ) : personPage ? (
            <PersonPage key={personPage['personId']} projectId={id} personId={personPage['personId']!} />
          ) : openedTest ? (
            <TestDetailScreen projectId={id} testId={openedTest} />
          ) : openedStudy ? (
            <StudyDetailScreen projectId={id} studyId={openedStudy} />
          ) : openedInterview ? (
            <InterviewDetailScreen projectId={id} interviewId={openedInterview} />
          ) : cutSource ? (
            <SourceCutScreen key={cutSource} projectId={id} studyId={sourceCut!['studyId']!} sourceId={cutSource} />
          ) : boardStudy ? (
            <BoardScreen key={boardStudy} projectId={id} studyId={boardStudy} />
          ) : (
            sectionScreen(section, id, project.nom)
          )}
        </ProjectLayout>
      </ProjectGate>
    );
  }

  // Compte de l'organisation (clé d'IA).
  if (path === '/compte') return <AccountPage />;

  // Suivi du produit : le numéro de version mène ici.
  if (path === '/versions') return <VersionsPage />;
  if (path === '/roadmap') return <RoadmapPage />;

  // Back-office de l'exploitant. L'adresse n'est pas un secret : pour tout autre
  // compte le serveur répond 404, et l'écran n'a rien à afficher.
  if (path === '/backoffice' || path.startsWith('/backoffice/')) {
    return <BackofficePage tab={matchRoute('/backoffice/:tab', path)?.['tab'] ?? ''} />;
  }

  return <PortfolioPage />;
}

/**
 * Porte d'entrée. Le questionnaire public de mesure zéro est aiguillé AVANT
 * toute vérification de session : son répondant (le client) n'a pas de compte,
 * et lui montrer l'écran de connexion serait une impasse.
 */
export function App() {
  return (
    <>
      <Page />
      {/* Sur TOUTES les pages, publiques comprises, tant que le choix n'est pas fait. */}
      <CookieBanner />
    </>
  );
}

function Page() {
  const { path } = useRouter();
  // Pages légales : publiques, lisibles SANS compte — c'est la condition pour
  // qu'un visiteur puisse les lire avant de s'inscrire.
  const legal = legalPageFor(path);
  if (legal) return legal;
  const publicQuestionnaire = matchRoute('/mesure0/r/:token', path);
  if (publicQuestionnaire?.['token']) return <PublicQuestionnaire token={publicQuestionnaire['token']} />;
  // Invitation d'équipe : la personne n'a pas encore de compte.
  const invitation = matchRoute('/invitation/:token', path);
  if (invitation?.['token']) return <AcceptInvitationPage token={invitation['token']} />;
  // Invitation à une séance : le participant n'aura jamais de compte.
  const rdv = matchRoute('/rdv/:token', path);
  if (rdv?.['token']) return <PublicInvitationPage token={rdv['token']} />;
  // Le questionnaire d'une étude (062) : on y répond sans compte, avec son lien.
  const questionnaire = matchRoute('/q/:token', path);
  if (questionnaire?.['token']) return <PublicStudyQuestionnaire token={questionnaire['token']} />;
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const me = useMe();
  const { path } = useRouter();
  // Mode maintenance (back-office) : quand il s'allume ou s'éteint, la session
  // est relue, car c'est elle qui dit si ce compte garde l'accès.
  const maintenance = useMaintenance();
  const active = maintenance.data?.active === true;
  const queryClient = useQueryClient();
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current === active) return;
    wasActive.current = active;
    void queryClient.invalidateQueries({ queryKey: keys.me });
  }, [active, queryClient]);

  // Premier appel en cours : on n'affiche NI l'écran de connexion NI
  // l'application. Trancher trop tôt ferait clignoter la page de connexion
  // devant un utilisateur déjà authentifié — le défaut le plus visible de
  // l'ancien frontend avant qu'il ne pose son drapeau `booting`.
  if (me.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Vérification de votre session…" />
      </div>
    );
  }

  // Le serveur est injoignable : on ne prétend pas que l'utilisateur est
  // déconnecté (il ne l'est peut-être pas), on propose de réessayer.
  if (me.isError) {
    return (
      <div className="app-loading">
        <div className="app-fallback">
          <p>Serveur injoignable.</p>
          <Button variant="ghost" onClick={() => void me.refetch()}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  // Le back-office a sa propre porte, reconnaissable au premier regard.
  if (!me.data) {
    if (path === '/backoffice' || path.startsWith('/backoffice/')) return <BackofficeLogin />;
    // Pendant la maintenance, ni connexion ni inscription : seule la porte du
    // back-office (ci-dessus) reste ouverte, pour l'exploitant.
    if (active && maintenance.data) return <MaintenancePage state={maintenance.data} />;
    // Un visiteur sans session qui arrive à la racine voit d'abord la page de
    // présentation (`public/decouvrir/`) ; elle mène à /connexion. Une adresse
    // profonde (lien partagé, signet), elle, montre directement la connexion.
    if (path === '/') return <GoToLanding />;
    return <AuthPage initialMode={path === '/demo' ? 'demo' : path === '/inscription' ? 'register' : 'login'} />;
  }

  // Déjà connecté : /connexion et /inscription n'ont plus d'objet.
  if (path === '/connexion' || path === '/inscription' || path === '/demo') return <GoHome />;
  // Connecté, mais sans accès gardé pendant la maintenance.
  if (active && maintenance.data && me.data.maintenanceBypass === false) {
    return <MaintenancePage state={maintenance.data} signedIn />;
  }

  return (
    <AppShell user={me.data.user} superAdmin={me.data.superAdmin === true}>
      <Routes />
    </AppShell>
  );
}

/** La présentation est une page statique, hors de l'application monopage :
 *  il faut un vrai chargement, pas une navigation interne. `replace` pour que
 *  « précédent » ne ramène pas sur une racine qui renverrait aussitôt ici. */
function GoToLanding() {
  useEffect(() => {
    window.location.replace('/decouvrir/');
  }, []);
  return null;
}

function GoHome() {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);
  return null;
}
